<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { GpBtn, GpModal, GpTabBar, type GpTabModelItem } from '@gportal/uikit';
import useBillingProfileFields from '@panel/components/user/billing-profiles/composables/useBillingProfileFields';
import { computed, provide, ref, watch } from 'vue';
import { faChevronRight, faFileInvoiceDollar } from '@fortawesome/pro-solid-svg-icons';
import AddCreditcardForm from '@panel/components/user/billing-profiles/creditcards/AddCreditcardForm.vue';
import { useUserStore } from 'gpcore-apikit/store/pinia/user';
import { type ServiceError } from 'gpcore-apikit/grpc/types';
import { BillingProfileInject } from '@panel/components/user/billing-profiles/symbols';
import type { BillingProfile } from 'gpcore-grpc/gpcore/api/cloud/v1/billing_pb';
import AddressForm from '@panel/components/user/billing-profiles/address/AddressForm.vue';
import useNotifications from 'gpcore-uikit/composables/useNotifications';

const props = withDefaults(defineProps<{ show?: boolean }>(), { show: false });

const emit = defineEmits<{ (e: 'close'): void; (e: 'created', billingProfileId: string): void }>();

const billingProfile = ref<BillingProfile>();

type WizardSteps = 'address' | 'payment';
const activeTab = ref<WizardSteps>('address');
const tabsModel = computed<Array<GpTabModelItem & { id: WizardSteps }>>(() => [
  {
    id: 'address',
    label: 'Address',
  },
  {
    id: 'payment',
    label: 'Payment',
    disabled: activeTab.value === 'address' && billingProfile.value == null,
  },
]);
const switchTabs = (id: WizardSteps, index: number) => {
  if (index !== tabsModel.value.length - 1) {
    activeTab.value = id;
  }
};

const { errors, form, updateFormField, handleSubmit, reset, supportsVatID } =
  useBillingProfileFields({
    billingProfile: null,
  });

const isSubmitting = ref(false);
const userStore = useUserStore();
const { addNotification } = useNotifications();
provide(BillingProfileInject, billingProfile);

const createAddress = handleSubmit(async () => {
  isSubmitting.value = true;
  try {
    const response = await userStore.createBillingProfile({
      ...form,
      countryCode: form.country.key,
    });
    billingProfile.value = response.billingProfile;

    activeTab.value = 'payment';
  } catch (error) {
    addNotification({
      type: 'danger',
      message: (error as ServiceError).message,
    });
  } finally {
    isSubmitting.value = false;
  }
});

const isCreditcardValid = ref(false);
const addCreditcardForm = ref<InstanceType<typeof AddCreditcardForm>>();
const createCreditcard = async () => {
  isSubmitting.value = true;
  await addCreditcardForm.value.submit();
  try {
    addNotification({
      message: 'The credit card has been added to your billing profile.',
      type: 'success',
    });
    emit('created', billingProfile.value.id);
  } catch (error) {
    addNotification({
      message: (error as ServiceError).message,
      type: 'danger',
    });
  } finally {
    isSubmitting.value = false;
  }
};

const continueCreate = async () => {
  if (activeTab.value === 'address') {
    if (billingProfile.value == null) {
      createAddress();
    } else {
      activeTab.value = 'payment';
    }
  } else {
    createCreditcard();
  }
};

const handleSkipAddCreditcard = () => {
  emit('created', billingProfile.value.id);
  billingProfile.value = null;
};

const handleClose = async () => {
  emit('close');
};

watch(
  () => props.show,
  () => {
    if (props.show) {
      activeTab.value = 'address';
      billingProfile.value = null;
      reset();
    }
  },
);
</script>

<template>
  <GpModal
    :is-opened="show"
    :close-on-backdrop="false"
    @confirm="() => continueCreate()"
    @close="handleClose"
    :header-icon="faFileInvoiceDollar"
    header-text="Setup billing profile"
  >
    <template #header>
      <div class="tw-p-4">
        <a
          href="#"
          v-if="activeTab === 'payment'"
          label="Back"
          size="sm"
          btn-type="flat"
          @click.prevent="switchTabs('address', 0)"
        >
          <FontAwesomeIcon :icon="faChevronRight" class="fa-rotate-180 me-2" />
          Back
        </a>
      </div>
    </template>
    <template #content>
      <div class="tw-mb-4 tw-px-4">
        <GpTabBar :tabs-model="tabsModel" v-model="activeTab" grow>
          <template #item="{ isActive, item, index }">
            <a
              v-if="!item.disabled"
              href="#"
              data-gp-element="gp-tab-entry"
              class="tw-flex tw-items-center tw-gap-2 tw-border-b tw-py-2 tw-font-bold"
              :class="{
                'tw-border-b-gp-cyan tw-text-gp-cyan': isActive,
                'tw-text-gp-mouse': !item.disabled && !isActive,
              }"
              @click.prevent="switchTabs(item.id as WizardSteps, index)"
            >
              <span
                class="tw-block tw-size-6 tw-rounded-full tw-py-1 tw-text-center tw-leading-none"
                :class="{
                  'tw-bg-gp-cyan tw-text-black': isActive,
                  'tw-bg-gp-mouse tw-text-black': !isActive,
                }"
              >
                {{ index + 1 }}
              </span>
              {{ item.label }}
            </a>
            <div
              v-else
              class="tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-b-gp-rhino tw-py-2 tw-font-bold tw-text-gp-rhino"
            >
              <span
                class="tw-block tw-size-6 tw-rounded-full tw-bg-gp-rhino tw-py-1 tw-text-center tw-leading-none tw-text-black"
              >
                {{ index + 1 }}
              </span>
              {{ item.label }}
            </div>
          </template>
        </GpTabBar>
        <div class="tw-mb-4">
          <template v-if="activeTab === 'address'">
            <AddressForm
              :form="form"
              :errors="errors"
              :supports-vat-id="supportsVatID"
              @updated:form="({ field, value }) => updateFormField(field, value)"
            />

            <GpBtn
              size="sm"
              class="tw-mt-8 tw-w-full"
              @click="continueCreate"
              :disabled="isSubmitting"
            >
              Continue
              <FontAwesomeIcon :icon="faChevronRight" class="tw-ml-2" />
            </GpBtn>
          </template>
          <template v-else-if="activeTab === 'payment'">
            <p class="tw-text-center">
              In order to process payment automatically, we recommend to add a credit card to your
              billing profile
            </p>
            <AddCreditcardForm
              ref="addCreditcardForm"
              @update:is-valid="(val) => (isCreditcardValid = val)"
            />
            <GpBtn
              class="tw-mt-4 tw-w-full"
              label="add credit card"
              :disabled="!isCreditcardValid || isSubmitting"
              size="sm"
              @click="continueCreate"
            />
            <hr class="tw-my-8" />
            <p class="tw-text-center">... or you can add one later.</p>
            <GpBtn
              class="tw-w-full"
              label="skip"
              size="sm"
              outline
              @click="handleSkipAddCreditcard"
            />
          </template>
        </div>
      </div>
    </template>
    <template #footer>
      <GpBtn
        class="tw-w-full"
        btn-type="cancel"
        size="sm"
        label="Cancel"
        :disabled="isSubmitting"
        @click="handleClose"
      />
    </template>
  </GpModal>
</template>
